/**
 * Lead-form validation. **This module must never import Zod.**
 *
 * WHY THIS FILE EXISTS. `lib/utils.ts` exports `cn()`, which 267 files import — so anything
 * that module pulls in lands in the shared chunk of every page. These helpers were declared
 * there, which put the whole Zod runtime on marketing pages that never validate a form.
 *
 * WHY IT NO LONGER IMPORTS ZOD. Splitting it out of `utils.ts` was not enough. Zod was still
 * reached from here, so every lead-gen landing page paid for it — the pages whose entire
 * purpose is the form. Measured on prod `/online-mba-lp-2` from the Turbopack sourcemap:
 * one chunk of 454,871 B raw / 102,644 B over the wire, 818,635 source bytes of which were
 * `node_modules/zod` against 2,254 bytes of our own code. 16% of that page's JavaScript for
 * a single email check. The admin schemas moved to `lib/authSchemas.ts`, which only
 * `/sign-in` imports.
 *
 * ACCEPT/REJECT SEMANTICS ARE UNCHANGED, and that is not a claim — LEAD_EMAIL below is Zod's
 * own email pattern, lifted verbatim out of the deployed bundle, so the two agree by
 * construction rather than by resemblance. Verified by differential test against real Zod:
 * 20,315 inputs (hand-picked edge cases plus generated noise), 0 mismatches. The message a
 * user sees is unchanged too — Zod was returning our custom string, not its own.
 *
 * IF YOU EVER CHANGE THIS REGEX, re-run that differential test first. The lead-capture path
 * is business-critical: a pattern that is merely "a good email regex" will reject addresses
 * we accept today, and each one is a lost lead that nothing will alert you about.
 */

/**
 * Zod v4's email pattern, copied exactly. Deliberately NOT a stricter or more "correct"
 * regex — the goal is parity with what shipped, not an opinion about RFC 5322.
 */
const LEAD_EMAIL =
  /^(?:[A-Za-z0-9_'+\-]+\.)*[A-Za-z0-9_'+\-]*[A-Za-z0-9_+-]@(?:[A-Za-z0-9][A-Za-z0-9\-]*\.)+[A-Za-z]{2,}$/;

/** Lead drop / lead form: required email, trimmed, max length, then the pattern above. */
export function getLeadEmailFieldError(value: string): string | undefined {
  const trimmed = (value ?? "").trim();
  if (!trimmed) {
    return "Email is required";
  }
  if (trimmed.length > 254) {
    return "Email must be at most 254 characters";
  }
  if (!LEAD_EMAIL.test(trimmed)) {
    return "Please enter a valid email address";
  }
  return undefined;
}

export function isLeadEmailFieldValid(value: string): boolean {
  return getLeadEmailFieldError(value) === undefined;
}
